/**
 * Sector operations — Construction Daily Progress Report (DPR) & Site Diary.
 * R39 B2.a (plan docs/plans/sector-operations.md row B2 :62, bullets :751-753; research C§2, C§9.1).
 */

import { isValidIsoDate, type ConDrawingRef, validateConDrawingRef } from './construction.js'

export type ConDprShift = 'DAY' | 'NIGHT' | 'SINGLE'

export const CON_DPR_SHIFTS: readonly ConDprShift[] = [
  'DAY',
  'NIGHT',
  'SINGLE',
] as const

export type ConDiaryKind =
  | 'WEATHER'
  | 'INSTRUCTION'
  | 'VISITOR'
  | 'INTERRUPTION'
  | 'DELIVERY'
  | 'SAFETY'
  | 'NOTE'

export const CON_DIARY_KINDS: readonly ConDiaryKind[] = [
  'WEATHER',
  'INSTRUCTION',
  'VISITOR',
  'INTERRUPTION',
  'DELIVERY',
  'SAFETY',
  'NOTE',
] as const

export interface ConDprGang {
  trade: string // <= 60
  headcount: number // 0..999
}

export interface ConDprEquipment {
  name: string // <= 60
  count: number // 0..99
  hours?: number | null // 0..24
}

export interface ConDprLine {
  lineId: string
  placeId: string // inside the project's SITE subtree
  trade: string // <= 60
  packageId?: string | null
  activity: string // <= 160
  plannedQty?: number | null // >= 0 if present
  doneQty: number // >= 0
  unit: string // <= 16
  gangs: ConDprGang[] // <= 50
  equipment: ConDprEquipment[] // <= 50
  hindranceRequestIds?: string[] // <= 20
  photoPaths?: string[] // <= 20
  drawingRefs?: ConDrawingRef[] // <= 20
}

export interface ConDprTotals {
  lines: number
  headcount: number
}

export interface ConDprBlock {
  projectId: string
  reportDate: string // YYYY-MM-DD
  shift: ConDprShift
  lines: ConDprLine[] // <= 200
  diaryEntryIds: string[] // snapshot at submit
  totals: ConDprTotals
}

export interface ConDiaryEntry {
  id: string
  workspaceId: string
  projectId: string
  placeId?: string | null
  kind: ConDiaryKind
  text: string // 1..1000
  occurredAt: unknown
  occurredDay?: string
  capturedAt: unknown
  receivedAt: unknown
  offline?: boolean
  photoPaths?: string[]
  requestId?: string | null
  authorUid: string
  supersedesId?: string | null
  createdAt: unknown
  updatedAt: unknown
}

// ---------------------------------------------------------------------------
// Pure Validators & Helpers
// ---------------------------------------------------------------------------

export function validateConDprShift(val: unknown): ConDprShift {
  if (typeof val !== 'string' || !CON_DPR_SHIFTS.includes(val as ConDprShift)) {
    throw new Error(`Invalid shift: must be one of ${CON_DPR_SHIFTS.join(', ')}`)
  }
  return val as ConDprShift
}

export function validateConDiaryKind(val: unknown): ConDiaryKind {
  if (typeof val !== 'string' || !CON_DIARY_KINDS.includes(val as ConDiaryKind)) {
    throw new Error(`Invalid diary kind: must be one of ${CON_DIARY_KINDS.join(', ')}`)
  }
  return val as ConDiaryKind
}

export function buildConDprRecordId(projectId: string, reportDate: string, shift: ConDprShift): string {
  if (!projectId || typeof projectId !== 'string' || !projectId.trim()) {
    throw new Error('projectId is required')
  }
  if (!reportDate || typeof reportDate !== 'string' || !isValidIsoDate(reportDate)) {
    throw new Error('reportDate must be valid YYYY-MM-DD')
  }
  const validShift = validateConDprShift(shift)
  return `dpr_${projectId.trim()}_${reportDate.trim()}_${validShift}`
}

export interface ParsedConDprRecordId {
  projectId: string
  reportDate: string
  shift: ConDprShift
}

export function parseConDprRecordId(recordId: unknown): ParsedConDprRecordId | null {
  if (typeof recordId !== 'string' || !recordId.startsWith('dpr_')) {
    return null
  }
  const match = /^dpr_(.+)_(\d{4}-\d{2}-\d{2})_([A-Za-z]+)$/.exec(recordId)
  if (!match) {
    return null
  }
  const [, projectId, reportDate, rawShift] = match
  if (!projectId || !projectId.trim()) {
    return null
  }
  if (!isValidIsoDate(reportDate)) {
    return null
  }
  if (!CON_DPR_SHIFTS.includes(rawShift as ConDprShift)) {
    return null
  }
  return {
    projectId,
    reportDate,
    shift: rawShift as ConDprShift,
  }
}

export function validateConDprGang(raw: unknown): ConDprGang {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Gang must be an object')
  }
  const g = raw as Partial<ConDprGang>
  if (typeof g.trade !== 'string' || !g.trade.trim()) {
    throw new Error('Gang trade must be a non-empty string')
  }
  const trade = g.trade.trim()
  if (trade.length > 60) {
    throw new Error('Gang trade cannot exceed 60 characters')
  }
  if (typeof g.headcount !== 'number' || !Number.isFinite(g.headcount)) {
    throw new Error('Gang headcount must be a number')
  }
  const headcount = Math.floor(g.headcount)
  if (headcount < 0 || headcount > 999) {
    throw new Error('Gang headcount must be between 0 and 999')
  }
  return { trade, headcount }
}

export function validateConDprEquipment(raw: unknown): ConDprEquipment {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Equipment must be an object')
  }
  const eq = raw as Partial<ConDprEquipment>
  if (typeof eq.name !== 'string' || !eq.name.trim()) {
    throw new Error('Equipment name must be a non-empty string')
  }
  const name = eq.name.trim()
  if (name.length > 60) {
    throw new Error('Equipment name cannot exceed 60 characters')
  }
  if (typeof eq.count !== 'number' || !Number.isFinite(eq.count)) {
    throw new Error('Equipment count must be a number')
  }
  const count = Math.floor(eq.count)
  if (count < 0 || count > 99) {
    throw new Error('Equipment count must be between 0 and 99')
  }

  let hours: number | null | undefined = undefined
  if (eq.hours !== undefined && eq.hours !== null) {
    if (typeof eq.hours !== 'number' || !Number.isFinite(eq.hours)) {
      throw new Error('Equipment hours must be a number')
    }
    if (eq.hours < 0 || eq.hours > 24) {
      throw new Error('Equipment hours must be between 0 and 24')
    }
    hours = eq.hours
  } else if (eq.hours === null) {
    hours = null
  }

  return { name, count, ...(hours !== undefined ? { hours } : {}) }
}

export function validateConDprLine(raw: unknown): ConDprLine {
  if (!raw || typeof raw !== 'object') {
    throw new Error('DPR line must be an object')
  }
  const l = raw as Partial<ConDprLine>
  if (typeof l.lineId !== 'string' || !l.lineId.trim()) {
    throw new Error('lineId is required and must be non-empty')
  }
  const lineId = l.lineId.trim()

  if (typeof l.placeId !== 'string' || !l.placeId.trim()) {
    throw new Error('placeId is required and must be non-empty')
  }
  const placeId = l.placeId.trim()

  if (typeof l.trade !== 'string' || !l.trade.trim()) {
    throw new Error('trade is required and must be non-empty')
  }
  const trade = l.trade.trim()
  if (trade.length > 60) {
    throw new Error('trade cannot exceed 60 characters')
  }

  const packageId =
    typeof l.packageId === 'string' && l.packageId.trim() ? l.packageId.trim() : null

  if (typeof l.activity !== 'string' || !l.activity.trim()) {
    throw new Error('activity is required and must be non-empty')
  }
  const activity = l.activity.trim()
  if (activity.length > 160) {
    throw new Error('activity cannot exceed 160 characters')
  }

  let plannedQty: number | null | undefined = undefined
  if (l.plannedQty !== undefined && l.plannedQty !== null) {
    if (typeof l.plannedQty !== 'number' || !Number.isFinite(l.plannedQty) || l.plannedQty < 0) {
      throw new Error('plannedQty must be a non-negative number')
    }
    plannedQty = l.plannedQty
  } else if (l.plannedQty === null) {
    plannedQty = null
  }

  if (typeof l.doneQty !== 'number' || !Number.isFinite(l.doneQty) || l.doneQty < 0) {
    throw new Error('doneQty must be a non-negative number')
  }
  const doneQty = l.doneQty

  if (typeof l.unit !== 'string' || !l.unit.trim()) {
    throw new Error('unit is required and must be non-empty')
  }
  const unit = l.unit.trim()
  if (unit.length > 16) {
    throw new Error('unit cannot exceed 16 characters')
  }

  const gangs: ConDprGang[] = []
  if (l.gangs) {
    if (!Array.isArray(l.gangs)) {
      throw new Error('gangs must be an array')
    }
    if (l.gangs.length > 50) {
      throw new Error('gangs cannot exceed 50 entries')
    }
    for (const g of l.gangs) {
      gangs.push(validateConDprGang(g))
    }
  }

  const equipment: ConDprEquipment[] = []
  if (l.equipment) {
    if (!Array.isArray(l.equipment)) {
      throw new Error('equipment must be an array')
    }
    if (l.equipment.length > 50) {
      throw new Error('equipment cannot exceed 50 entries')
    }
    for (const eq of l.equipment) {
      equipment.push(validateConDprEquipment(eq))
    }
  }

  const hindranceRequestIds: string[] = []
  if (l.hindranceRequestIds) {
    if (!Array.isArray(l.hindranceRequestIds)) {
      throw new Error('hindranceRequestIds must be an array')
    }
    if (l.hindranceRequestIds.length > 20) {
      throw new Error('hindranceRequestIds cannot exceed 20 entries')
    }
    for (const reqId of l.hindranceRequestIds) {
      if (typeof reqId === 'string' && reqId.trim()) {
        hindranceRequestIds.push(reqId.trim())
      }
    }
  }

  const photoPaths: string[] = []
  if (l.photoPaths) {
    if (!Array.isArray(l.photoPaths)) {
      throw new Error('photoPaths must be an array')
    }
    if (l.photoPaths.length > 20) {
      throw new Error('photoPaths cannot exceed 20 entries')
    }
    for (const p of l.photoPaths) {
      if (typeof p === 'string' && p.trim()) {
        photoPaths.push(p.trim())
      }
    }
  }

  const drawingRefs: ConDrawingRef[] = []
  if (l.drawingRefs) {
    if (!Array.isArray(l.drawingRefs)) {
      throw new Error('drawingRefs must be an array')
    }
    if (l.drawingRefs.length > 20) {
      throw new Error('drawingRefs cannot exceed 20 entries')
    }
    for (const d of l.drawingRefs) {
      drawingRefs.push(validateConDrawingRef(d))
    }
  }

  return {
    lineId,
    placeId,
    trade,
    ...(packageId ? { packageId } : {}),
    activity,
    ...(plannedQty !== undefined ? { plannedQty } : {}),
    doneQty,
    unit,
    gangs,
    equipment,
    ...(hindranceRequestIds.length > 0 ? { hindranceRequestIds } : {}),
    ...(photoPaths.length > 0 ? { photoPaths } : {}),
    ...(drawingRefs.length > 0 ? { drawingRefs } : {}),
  }
}

export function validateConDprLines(raw: unknown): ConDprLine[] {
  if (!Array.isArray(raw)) {
    throw new Error('DPR lines must be an array')
  }
  if (raw.length > 200) {
    throw new Error('DPR lines cannot exceed 200 entries')
  }
  const lines: ConDprLine[] = []
  const seenLineIds = new Set<string>()

  for (const item of raw) {
    const validated = validateConDprLine(item)
    if (seenLineIds.has(validated.lineId)) {
      throw new Error(`Duplicate lineId: ${validated.lineId}`)
    }
    seenLineIds.add(validated.lineId)
    lines.push(validated)
  }
  return lines
}

export function computeConDprTotals(lines: ConDprLine[]): ConDprTotals {
  let headcount = 0
  for (const line of lines) {
    for (const gang of line.gangs) {
      headcount += gang.headcount
    }
  }
  return {
    lines: lines.length,
    headcount,
  }
}

export function validateConDiaryText(val: unknown): string {
  if (typeof val !== 'string' || !val.trim()) {
    throw new Error('Diary text must be a non-empty string')
  }
  const clean = val.trim()
  if (clean.length > 1000) {
    throw new Error('Diary text cannot exceed 1000 characters')
  }
  return clean
}
